import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, Check, AlertCircle } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({
    type: '',  // 'success', 'error', 'loading'
    message: ''
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // URL de l'API - utilise la variable d'environnement ou l'URL de production par défaut
  const API_URL = import.meta.env.VITE_API_URL || 'https://web.cfa-montargis.com';

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation email simple
    if (!email || !email.includes('@')) {
      setStatus({
        type: 'error',
        message: 'Veuillez saisir un email valide'
      });
      return;
    }
    
    // Vérification du captcha
    if (!captchaToken) {
      setStatus({
        type: 'error',
        message: 'Veuillez valider le captcha'
      });
      return;
    }
    
    // Indiquer que la soumission est en cours
    setStatus({
      type: 'loading',
      message: 'Inscription en cours...'
    });
    
    try {
      // Appel à l'API backend pour vérifier le captcha
      const response = await fetch(`${API_URL}/api/newsletter-subscribe`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          captchaToken: captchaToken
        })
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        setStatus({
          type: 'error',
          message: data.error || 'Une erreur est survenue'
        });
        
        // Réinitialiser le captcha en cas d'erreur
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
        return;
      }
      
      // Succès
      setStatus({
        type: 'success',
        message: data.message
      });
      
      setEmail(''); // Réinitialiser le formulaire
      setCaptchaToken(null); // Réinitialiser le token captcha
      
      // Réinitialiser le widget captcha
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      setStatus({
        type: 'error',
        message: 'Impossible de se connecter au serveur. Veuillez réessayer.'
      });
      
      // Réinitialiser le captcha en cas d'erreur
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.3 }}
      className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 rounded-lg p-5 border border-slate-700/50"
    >
      <h4 className="text-white font-medium mb-3 flex items-center">
        <Mail className="w-4 h-4 mr-2" />
        Newsletter
      </h4>
      <p className="text-gray-300 text-sm mb-4">
        Inscrivez-vous pour recevoir nos actualités et événements à venir.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex">
          <input 
            type="email" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre email" 
            className="bg-slate-800/80 text-white px-4 py-2 rounded-l-md flex-grow focus:outline-none focus:ring-2 focus:ring-blue-500 border border-slate-700" 
            disabled={status.type === 'loading' || status.type === 'success'}
            autoComplete="email"
            required
          />
          <motion.button 
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 rounded-r-md transition-colors flex items-center justify-center ${
              status.type === 'loading' 
                ? 'bg-gray-600 cursor-wait' 
                : status.type === 'success'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
            disabled={status.type === 'loading' || status.type === 'success'}
          >
            {status.type === 'loading' ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : status.type === 'success' ? (
              <Check className="w-5 h-5" />
            ) : (
              "S'inscrire"
            )}
          </motion.button>
        </div>
        
        {/* ReCAPTCHA v2 */}
        <div className={`transition-opacity duration-300 ${status.type === 'success' ? 'opacity-0' : 'opacity-100'}`}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            theme="dark"
            size="normal"
            className="transform scale-90 origin-left mt-3"
          />
          <p className="text-xs text-gray-400 mt-1">
            Ce site est protégé par reCAPTCHA et les <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">règles de confidentialité</a> et <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">conditions d'utilisation</a> de Google s'appliquent.
          </p>
        </div>
        
        {status.message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-3 rounded text-sm ${
              status.type === 'error'
                ? 'bg-red-900/20 text-red-400'
                : status.type === 'success'
                  ? 'bg-green-900/20 text-green-400'
                  : 'bg-blue-900/20 text-blue-400'
            }`}
          >
            {status.type === 'error' ? (
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
            ) : status.type === 'success' ? (
              <Check className="w-4 h-4 flex-shrink-0" />
            ) : null}
            <span>{status.message}</span>
          </motion.div>
        )}
      </form>
    </motion.div>
  );
};

export default NewsletterForm;